import React from 'react'
import { Box } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'

import { ContactInfoIcon } from '~assets/icons'
import { Button, InputLabel, RemoveEntryButton } from '~components/common'
import { AttributionCreate } from '~types'

import AttributionEntryBox from './AttributionEntryBox'

interface AttributionInputRow {
  id: string
  name: string
  value: string
  isValid: boolean
}

type AttributionInputTableProps = {
  setAttributions: React.Dispatch<React.SetStateAction<AttributionCreate[]>>
  onValidationChange?: (isValid: boolean) => void
}

const AttributionInputTable: React.FC<AttributionInputTableProps> = ({
  setAttributions,
  onValidationChange,
}) => {
  const [inputRows, setInputRows] = React.useState<AttributionInputRow[]>([
    { id: uuidv4(), name: '', value: '', isValid: false },
  ])

  const _areAllAttributionsValid = React.useMemo(() => {
    const isValid = inputRows.every(
      (row) =>
        (row.name.trim() === '' && row.value.trim() === '') ||
        (row.name.trim() !== '' && row.value.trim() !== ''),
    )

    onValidationChange?.(isValid)
    return isValid
  }, [inputRows, onValidationChange])

  const addAttributionInput = (): void => {
    setInputRows((prev) => [...prev, { id: uuidv4(), name: '', value: '', isValid: false }])
  }

  const removeAttributionInput = (id: string): void => {
    setInputRows((prev) => prev.filter((row) => row.id !== id))
  }

  const handleAttributionChange = (id: string, field: 'name' | 'value', newValue: string): void => {
    setInputRows((prev) => {
      const newRows = prev.map((row) => {
        if (row.id !== id) return row

        const updatedRow = {
          ...row,
          [field]: newValue,
        }

        updatedRow.isValid = updatedRow.name.trim() !== '' && updatedRow.value.trim() !== ''

        return updatedRow
      })

      const validAttributions = newRows
        .filter((row) => row.isValid)
        .map((row) => ({
          attribute_id: row.id,
          name: row.name,
          value: row.value,
        }))

      setAttributions(validAttributions)
      return newRows
    })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 2,
          mb: 2,
          borderBottom: '1px solid #ccc',
        }}
      >
        <InputLabel label='Additional Data' isOptional icon={ContactInfoIcon} />

        {/* Attribution input rows */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          {inputRows.map((row) => (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                gap: 2,
                mr: 1,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, flexGrow: 1 }}>
                <AttributionEntryBox
                  id={row.id}
                  name={row.name}
                  value={row.value}
                  onChange={handleAttributionChange}
                />
              </Box>
              <Box sx={{ mt: 3 }}>
                <RemoveEntryButton
                  row={row}
                  onRemove={() => removeAttributionInput(row.id)}
                  showWarning={false}
                  showConfirmation={false}
                />
              </Box>
            </Box>
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Box mr={8}>
              <Button
                size='small'
                variant='outlined'
                color='secondary'
                onClick={addAttributionInput}
              >
                Add
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default AttributionInputTable
